import axios, { AxiosInstance, AxiosError } from 'axios';

/**
 * Centralized API client for Admoo Device Management API
 * Handles base configuration and request/response interceptors
 */
export class ApiClient {
  private readonly axiosInstance: AxiosInstance;

  private token: string | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to attach auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor for consistent error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Extract error message from response
        const errorMessage = this.extractErrorMessage(error);
        return Promise.reject(new Error(errorMessage));
      },
    );
  }

  /**
   * Set the authentication token for API requests
   * Should be called with the Okta access token
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Dynamically update the base URL (e.g. to target a different environment)
   */
  setBaseURL(url: string): void {
    this.axiosInstance.defaults.baseURL = url;
  }

  /**
   * Get the axios instance for making requests
   */
  getClient(): AxiosInstance {
    return this.axiosInstance;
  }

  /**
   * Extract error message from axios error response
   */
  private extractErrorMessage(error: AxiosError): string {
    if (error.response?.data) {
      const data = error.response.data as any;
      if (data.error) return data.error;
      if (data.message) return data.message;
      if (typeof data === 'string') return data;
    }
    return error.message || 'An unexpected error occurred';
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
